import { rm, access } from 'node:fs/promises';
import { join } from 'node:path';
import { Command } from 'commander';
import { isGitUrl } from '../../plugin/git-clone.js';
import { createServices, destroyServices } from '../../services/index.js';
import { checkStoreModelCompatibility } from '../../utils/model-validation.js';
import type { Store, StoreType } from '../../types/store.js';
import type { GlobalOptions } from '../program.js';

/** Health check severity level */
type HealthSeverity = 'error' | 'warning';

/** Health check issue code */
type HealthIssueCode = 'PATH_NOT_FOUND' | 'SCHEMA_V1' | 'MODEL_MISMATCH';

/** A single health check issue */
interface HealthIssue {
  severity: HealthSeverity;
  code: HealthIssueCode;
  message: string;
  resolution: string;
}

/** Health check result for a single store */
interface HealthResult {
  storeId: string;
  storeName: string;
  storeType: StoreType;
  status: 'healthy' | 'warning' | 'error';
  issues: HealthIssue[];
}

/**
 * Check if a path exists
 */
async function pathExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check health of a single store
 */
async function checkStoreHealth(store: Store, currentModelId: string): Promise<HealthResult> {
  const issues: HealthIssue[] = [];

  // 1. Path check - file/repo only
  if (store.type === 'file' || store.type === 'repo') {
    const exists = await pathExists(store.path);
    if (!exists) {
      issues.push({
        severity: 'error',
        code: 'PATH_NOT_FOUND',
        message: `Path does not exist: ${store.path}`,
        resolution: 'Re-create store or fix projectRoot if path was relative',
      });
    }
  }

  // 2. Model compatibility check (reuses existing validation)
  const modelCheck = checkStoreModelCompatibility(store, { currentModelId });
  if (!modelCheck.compatible) {
    const isSchemaV1 = modelCheck.reason?.includes('v1') === true;
    issues.push({
      severity: 'warning',
      code: isSchemaV1 ? 'SCHEMA_V1' : 'MODEL_MISMATCH',
      message: modelCheck.reason ?? 'Model incompatibility',
      resolution: `Run: bluera-knowledge index ${store.name}`,
    });
  }

  // Determine overall status
  const hasError = issues.some((i) => i.severity === 'error');
  const hasWarning = issues.some((i) => i.severity === 'warning');
  const status = hasError ? 'error' : hasWarning ? 'warning' : 'healthy';

  return {
    storeId: store.id,
    storeName: store.name,
    storeType: store.type,
    status,
    issues,
  };
}

export function createStoreCommand(getOptions: () => GlobalOptions): Command {
  const store = new Command('store').description(
    'Manage knowledge stores (collections of indexed documents)'
  );

  store
    .command('list')
    .description('Show all stores with their type (file/repo/web) and ID')
    .option('-t, --type <type>', 'Filter by type: file, repo, or web')
    .action(async (options: { type?: StoreType }) => {
      const globalOpts = getOptions();
      const services = await createServices(
        globalOpts.config,
        globalOpts.dataDir,
        globalOpts.projectRoot
      );
      try {
        const stores = await services.store.list(options.type);

        if (globalOpts.format === 'json') {
          console.log(JSON.stringify(stores, null, 2));
        } else if (globalOpts.quiet === true) {
          // Quiet mode: just list store names, one per line
          for (const s of stores) {
            console.log(s.name);
          }
        } else {
          if (stores.length === 0) {
            console.log('No stores found.');
          } else {
            console.log('\nStores:\n');
            for (const s of stores) {
              console.log(`  ${s.name} (${s.type}) - ${s.id}`);
            }
            console.log('');
          }
        }
      } finally {
        await destroyServices(services);
      }
    });

  store
    .command('create <name>')
    .description('Create a new store pointing to a local path or URL')
    .requiredOption(
      '-t, --type <type>',
      'Store type: file (local dir), repo (git), web (crawled site)'
    )
    .requiredOption('-s, --source <path>', 'Local path for file/repo stores, URL for web stores')
    .option('-b, --branch <branch>', 'Git branch to clone (repo stores only)')
    .option('-d, --description <desc>', 'Optional description for the store')
    .option('--tags <tags>', 'Comma-separated tags for filtering')
    .action(
      async (
        name: string,
        options: {
          type: StoreType;
          source: string;
          branch?: string;
          description?: string;
          tags?: string;
        }
      ) => {
        const globalOpts = getOptions();
        const services = await createServices(
          globalOpts.config,
          globalOpts.dataDir,
          globalOpts.projectRoot
        );
        let exitCode = 0;
        try {
          // Detect if source is a URL (for repo stores that should clone from remote)
          // Supports http://, https://, and git@ SSH URLs
          const isUrl = isGitUrl(options.source);
          const result = await services.store.create({
            name,
            type: options.type,
            path:
              options.type === 'file' || (options.type === 'repo' && !isUrl)
                ? options.source
                : undefined,
            url:
              options.type === 'web' || (options.type === 'repo' && isUrl)
                ? options.source
                : undefined,
            branch: options.type === 'repo' ? options.branch : undefined,
            description: options.description,
            tags: options.tags?.split(',').map((t) => t.trim()),
          });

          if (result.success) {
            if (globalOpts.format === 'json') {
              console.log(JSON.stringify(result.data, null, 2));
            } else {
              console.log(`\nCreated store: ${result.data.name} (${result.data.id})\n`);
            }
          } else {
            console.error(`Error: ${result.error.message}`);
            exitCode = 1;
          }
        } finally {
          await destroyServices(services);
        }
        if (exitCode !== 0) {
          // Set exit code and let Node.js exit naturally after event loop drains
          // Using process.exit() causes mutex crashes from native code (LanceDB, tree-sitter)
          process.exitCode = exitCode;
        }
      }
    );

  store
    .command('info <store>')
    .description('Show store details: ID, type, path/URL, timestamps')
    .action(async (storeIdOrName: string) => {
      const globalOpts = getOptions();
      const services = await createServices(
        globalOpts.config,
        globalOpts.dataDir,
        globalOpts.projectRoot
      );
      let exitCode = 0;
      storeInfo: try {
        const s = await services.store.getByIdOrName(storeIdOrName);

        if (s === undefined) {
          console.error(`Error: Store not found: ${storeIdOrName}`);
          exitCode = 3;
          break storeInfo;
        }

        if (globalOpts.format === 'json') {
          console.log(JSON.stringify(s, null, 2));
        } else {
          console.log(`\nStore: ${s.name}`);
          console.log(`  ID: ${s.id}`);
          console.log(`  Type: ${s.type}`);
          if ('path' in s) console.log(`  Path: ${s.path}`);
          if ('url' in s && s.url !== undefined) console.log(`  URL: ${s.url}`);
          if (s.description !== undefined) console.log(`  Description: ${s.description}`);
          console.log(`  Created: ${s.createdAt.toISOString()}`);
          console.log(`  Updated: ${s.updatedAt.toISOString()}`);
          console.log('');
        }
      } finally {
        await destroyServices(services);
      }
      if (exitCode !== 0) {
        // Set exit code and let Node.js exit naturally after event loop drains
        // Using process.exit() causes mutex crashes from native code (LanceDB, tree-sitter)
        process.exitCode = exitCode;
      }
    });

  store
    .command('delete <store>')
    .description('Remove store and its indexed documents from LanceDB')
    .option('-f, --force', 'Delete without confirmation prompt')
    .option('-y, --yes', 'Alias for --force')
    .action(async (storeIdOrName: string, options: { force?: boolean; yes?: boolean }) => {
      const globalOpts = getOptions();
      const services = await createServices(
        globalOpts.config,
        globalOpts.dataDir,
        globalOpts.projectRoot
      );
      let exitCode = 0;
      storeDelete: try {
        const s = await services.store.getByIdOrName(storeIdOrName);

        if (s === undefined) {
          console.error(`Error: Store not found: ${storeIdOrName}`);
          exitCode = 3;
          break storeDelete;
        }

        // Require --force or -y in non-TTY mode, prompt in TTY mode
        const skipConfirmation = options.force === true || options.yes === true;
        if (!skipConfirmation) {
          if (!process.stdin.isTTY) {
            console.error(
              'Error: Use --force or -y to delete without confirmation in non-interactive mode'
            );
            exitCode = 1;
            break storeDelete;
          }
          // Interactive confirmation
          const readline = await import('node:readline');
          const rl = readline.createInterface({
            input: process.stdin,
            output: process.stdout,
          });
          const answer = await new Promise<string>((resolve) => {
            rl.question(`Delete store "${s.name}"? [y/N] `, resolve);
          });
          rl.close();
          if (answer.toLowerCase() !== 'y' && answer.toLowerCase() !== 'yes') {
            console.log('Cancelled.');
            // exitCode stays 0 for user-initiated cancellation
            break storeDelete;
          }
        }

        // Delete LanceDB table first (so searches don't return results for deleted store)
        await services.lance.deleteStore(s.id);

        // Delete code graph file
        await services.codeGraph.deleteGraph(s.id);

        // Delete manifest file (matches MCP/server behavior)
        await services.manifest.delete(s.id);

        // For repo stores cloned from URL, remove the cloned directory
        if (s.type === 'repo' && 'url' in s && s.url !== undefined) {
          const dataDir = services.config.resolveDataDir();
          const repoPath = join(dataDir, 'repos', s.id);
          await rm(repoPath, { recursive: true, force: true });
        }

        // Delete from registry last
        const result = await services.store.delete(s.id);

        if (result.success) {
          console.log(`Deleted store: ${s.name}`);
        } else {
          console.error(`Error: ${result.error.message}`);
          exitCode = 1;
        }
      } finally {
        await destroyServices(services);
      }
      if (exitCode !== 0) {
        // Set exit code and let Node.js exit naturally after event loop drains
        // Using process.exit() causes mutex crashes from native code (LanceDB, tree-sitter)
        process.exitCode = exitCode;
      }
    });

  store
    .command('health')
    .description('Check health of all stores: path existence, model compatibility')
    .option('-s, --store <name>', 'Check a specific store only')
    .action(async (options: { store?: string }) => {
      const globalOpts = getOptions();
      const services = await createServices(
        globalOpts.config,
        globalOpts.dataDir,
        globalOpts.projectRoot
      );
      let exitCode = 0;
      try {
        const currentModelId = services.store.getCurrentModelId();
        let storesToCheck: Store[];

        if (options.store !== undefined) {
          const s = await services.store.getByIdOrName(options.store);
          if (s === undefined) {
            console.error(`Error: Store not found: ${options.store}`);
            exitCode = 1;
            return;
          }
          storesToCheck = [s];
        } else {
          storesToCheck = await services.store.list();
        }

        if (storesToCheck.length === 0) {
          if (globalOpts.format === 'json') {
            console.log(JSON.stringify({ stores: [], exitCode: 0 }, null, 2));
          } else if (globalOpts.quiet !== true) {
            console.log('No stores found.');
          }
          return;
        }

        // Check health of each store
        const results: HealthResult[] = [];
        for (const s of storesToCheck) {
          const result = await checkStoreHealth(s, currentModelId);
          results.push(result);
        }

        // Determine exit code: 1 for any error, 2 for warning-only, 0 for healthy
        const hasError = results.some((r) => r.status === 'error');
        const hasWarning = results.some((r) => r.status === 'warning');
        exitCode = hasError ? 1 : hasWarning ? 2 : 0;

        // Output results
        if (globalOpts.format === 'json') {
          console.log(JSON.stringify({ stores: results, exitCode }, null, 2));
        } else if (globalOpts.quiet === true) {
          // Quiet mode: no output, just exit code
        } else {
          // Human-readable output
          console.log('\nStore Health Check\n');
          for (const result of results) {
            const statusIcon =
              result.status === 'healthy' ? '✓' : result.status === 'warning' ? '⚠' : '✗';
            const statusColor =
              result.status === 'healthy'
                ? '\x1b[32m'
                : result.status === 'warning'
                  ? '\x1b[33m'
                  : '\x1b[31m';
            const reset = '\x1b[0m';

            console.log(
              `  ${statusColor}${statusIcon}${reset} ${result.storeName} (${result.storeType})`
            );
            for (const issue of result.issues) {
              const issueIcon = issue.severity === 'error' ? '  ✗' : '  ⚠';
              console.log(`    ${issueIcon} ${issue.message}`);
              console.log(`      Fix: ${issue.resolution}`);
            }
          }

          // Summary
          const healthy = results.filter((r) => r.status === 'healthy').length;
          const warnings = results.filter((r) => r.status === 'warning').length;
          const errors = results.filter((r) => r.status === 'error').length;
          console.log('');
          console.log(
            `Summary: ${String(healthy)} healthy, ${String(warnings)} warnings, ${String(errors)} errors`
          );
          console.log('');
        }
      } finally {
        await destroyServices(services);
      }
      if (exitCode !== 0) {
        process.exitCode = exitCode;
      }
    });

  return store;
}
